import {Location, useParams} from "react-router";
import {DependencyList, EffectCallback, useContext, useEffect, useMemo, useRef} from "react";
import {classPrefix, composeURLString, log, parseURLString, qsParse, searchComponentsSchema} from "~/lib";
import type {OpenAPI3} from "openapi-typescript";
import type {JSONSchema7} from "json-schema";
import {AllowedThemes, FormFieldParams, RetURLParam} from "~/misc.types";
import {URLScheme} from "~/url.types";
import {StyleSelector, StyleSelectorCreator, StyleSelectorStyles} from "~/styles.types";
import {ClassNameArray} from "~/classes";
import StylesContext from "~/context/StylesContext";
import {StylesContextProps} from "~/context/StylesContext.props";
import {RouteParams} from "~/hooks.types";

export function useRetURL(urlString: string, location: Location | string, retURLParam: RetURLParam): string {
    const locationKey = typeof location === "string" ? location : location.key;
    return useMemo(() => {
        if (retURLParam) {
            const urlScheme = parseURLString(urlString);
            if (urlScheme) {
                let locationScheme: Partial<URLScheme> | false;
                if (typeof location === "string") {
                    locationScheme = parseURLString(location);
                    if (locationScheme && !locationScheme.query) {
                        locationScheme.query = {};
                    }
                } else {
                    locationScheme = {
                        ...location,
                        query: qsParse(location.search),
                    };
                }
                let retURLQueryParam = typeof retURLParam === "function" ? retURLParam(urlScheme, locationScheme, true) : retURLParam;
                if (retURLQueryParam === true) {
                    retURLQueryParam = "_ret";
                }
                if (retURLQueryParam && locationScheme && locationScheme.query && locationScheme.query[retURLQueryParam]) {
                    locationScheme = parseURLString(locationScheme.query[retURLQueryParam]);
                }
                if (locationScheme) {
                    if (typeof retURLParam === "function") {
                        retURLQueryParam = retURLParam(urlScheme, locationScheme);
                        if (retURLQueryParam === true) {
                            retURLQueryParam = "_ret";
                        }
                    }

                    if (retURLQueryParam) {
                        if (urlScheme.pathname !== locationScheme.pathname) {
                            if (!urlScheme.query) {
                                urlScheme.query = {};
                            }
                            urlScheme.query[retURLQueryParam] = composeURLString(locationScheme);
                            urlString = composeURLString(urlScheme);
                        }
                    }
                }
            }
        }

        return urlString;
    }, [urlString, locationKey, retURLParam]);
}

export function useTheme(urlString: string, theme: AllowedThemes | undefined): string {
    return useMemo(() => {
        if (theme) {
            const urlScheme = parseURLString(urlString);
            if (urlScheme) {
                if (!urlScheme.query) {
                    urlScheme.query = {};
                }
                urlScheme.query["_theme"] = theme;
                urlString = composeURLString(urlScheme);
            }
        }

        return urlString;
    }, [urlString, theme]);
}

export function useOnMountUnsafe(effect: EffectCallback, deps: DependencyList = [], refVal: boolean | string | number = true) {
    const initialized = useRef<boolean | string | number>(false);

    useEffect(() => {
        if (!initialized.current || initialized.current !== refVal) {
            initialized.current = refVal;
            effect();
        }
    }, deps);
}

export function useFormFieldParamsFromSchema<FormValues extends Record<string, any>>(openapiSchema: OpenAPI3 | undefined, schemaName: string | string[]): FormFieldParams<FormValues> {
    return useMemo<FormFieldParams<FormValues>>(
        () => {
            const props: FormFieldParams<FormValues> = {};
            if (openapiSchema) {
                const schema: JSONSchema7 | undefined = searchComponentsSchema(openapiSchema, schemaName, true);

                if (schema) {
                    if (schema.type === "object" && schema.properties) {
                        let requiredProps: string[] = [];
                        if (schema.required && schema.required.length) {
                            requiredProps = schema.required;
                        } else if (typeof schema.required === "object") {
                            requiredProps = Object.values(schema.required);
                        }
                        Object.entries(schema.properties).forEach(([p, propSchema]) => {
                            if (typeof propSchema === "object") {
                                const fieldParams: FormFieldParams<FormValues>[keyof FormFieldParams<FormValues>] = {
                                    type: "text",
                                    required: requiredProps.includes(p),
                                    validators: [],
                                    attributes: {},
                                };

                                switch (propSchema.format) {
                                    case "email":
                                    case "password":
                                        fieldParams.type = propSchema.format;
                                        break;
                                    case "uuid":
                                        fieldParams.validators.push(val => typeof val !== "undefined" ? ((val + "").match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/) ? undefined : "Invalid UUID!") : undefined);
                                        break;
                                }
                                if (fieldParams.required) {
                                    fieldParams.attributes.required = true;
                                    fieldParams.validators.push(val => val ? undefined : "Required");
                                }
                                if (typeof propSchema.minLength === "number") {
                                    fieldParams.attributes.minLength = propSchema.minLength;
                                    fieldParams.validators.push(val => {
                                        const len: number = typeof val === "string" ? val.length : 0;
                                        return len >= (propSchema.minLength || 0) ? undefined : `Value length should be grater than ${propSchema.minLength}`;
                                    });
                                }
                                if (typeof propSchema.maxLength === "number") {
                                    fieldParams.attributes.maxLength = propSchema.maxLength;
                                    fieldParams.validators.push(val => {
                                        const len: number = typeof val === "string" ? val.length : 0;
                                        return len <= (propSchema.maxLength || 0) ? undefined : `Value length should be less than ${propSchema.maxLength}`;
                                    });
                                }

                                props[p as keyof FormFieldParams<FormValues>] = fieldParams;

                                if (propSchema.format === "password" && fieldParams) {
                                    const confirmFieldParams = {...fieldParams};
                                    confirmFieldParams.validators = [...fieldParams.validators];
                                    confirmFieldParams.validators.push((val, values) => {
                                        return val === values[p] ? undefined : `Values of "${p}" and "${p}-confirm" not equals`;
                                    });

                                    props[(`${p}-confirm`) as keyof FormFieldParams<FormValues>] = confirmFieldParams;
                                }
                            }
                        });
                    }
                }
            }

            return props;
        },
        [openapiSchema]
    );
}

export const stylesSelector: StyleSelectorCreator = (name: (string|boolean)|(string|boolean)[], styles: StyleSelectorStyles, warnMissing: boolean): ClassNameArray => {
    let ret: ClassNameArray = [];
    if (name instanceof Array && name.length === 1) {
        name = name[0];
    }
    if (name instanceof Array && name.length > 1) {
        ret = (new ClassNameArray()).concat(...(name.map(n => stylesSelector(n, styles, warnMissing)).filter(c => !!c)));
    } else if (typeof name === "string") {
        const stylesArray = styles instanceof Array ? styles : [styles];
        ret = stylesArray.reduce(
            (ret, styles) => {
                if (styles) {
                    let foundStyle: string | string[] | undefined = undefined;
                    if (typeof styles === "object") {
                        foundStyle = styles[classPrefix(<string>name)] || undefined;
                    } else if (typeof styles === "function") {
                        foundStyle = styles(<string>name) || undefined;
                    }
                    if (foundStyle && foundStyle.length) {
                        ret = ret.concat(foundStyle);
                    }
                }

                return ret;
            },
            (new ClassNameArray())
        );
        if (warnMissing && !ret.length) {
            log(`Missing class name '${name}'`);
        }
    }

    return ret;
};

export function useStylesSelector(styles: StyleSelectorStyles, warnMissing: boolean = false): StyleSelector {
    const {styles: contextStyles} = useContext(StylesContext) as StylesContextProps;
    const mergedStyles = contextStyles ? (styles instanceof Array ? [...styles, contextStyles] : [styles, contextStyles]) : styles;
    return (...name: (string | boolean)[]) => stylesSelector(name, mergedStyles, warnMissing);
}

export function useRouteParams(): RouteParams {
    const params = useParams();
    const routeParams: RouteParams = {...params};
    if (Object.hasOwn(params, "edit")) {
        routeParams.edit = params.edit === "edit";
    }

    return routeParams;
}